import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import ImageField from "./ImageField";

const PAGES = [
  { key: "home", label: "Página Inicial" },
  { key: "quem_somos", label: "Quem Somos" },
  { key: "areas", label: "Áreas de Atuação (capa)" },
  { key: "servicos", label: "Serviços Jurídicos" },
  { key: "para_quem", label: "Para Quem Atuamos" },
  { key: "palestras", label: "Palestras e Treinamentos" },
  { key: "faca_parte", label: "Faça Parte" },
  { key: "contato", label: "Contato" },
  { key: "privacidade", label: "Política de Privacidade" },
];

const FIELD_LABELS = {
  hero_eyebrow: "Sobretítulo do hero", hero_tagline: "Linha dourada do hero", hero_title: "Título do hero",
  hero_text: "Texto do hero", hero_image: "Fotografia do hero", cta_areas: "Botão — áreas de atuação",
  cta_servicos: "Botão — serviços", cta_conteudos: "Botão — conteúdos", cta_contato: "Botão — contato",
  marquee_words: "Palavras do letreiro (separe por ;)", sobre_title: "Título — sobre", sobre_text: "Texto — sobre",
  sobre_image: "Imagem — sobre", areas_title: "Título — áreas", areas_text: "Texto — áreas",
  servicos_title: "Título — serviços", servicos_text: "Texto — serviços", publicos_title: "Título — públicos",
  publicos_text: "Texto — públicos", conteudos_title: "Título — conteúdos", conteudos_text: "Texto — conteúdos",
  band_title: "Faixa de contato — título", band_button: "Faixa de contato — botão",
  identidade_title: "Identidade — título", identidade_text: "Identidade — texto",
  proposta_title: "Proposta — título", proposta_text: "Proposta — texto",
  abordagem_title: "Abordagem — título", abordagem_text: "Abordagem — texto",
  compromisso_title: "Compromisso — título", compromisso_text: "Compromisso — texto",
  advogada_title: "Nome da advogada", advogada_text: "Texto da advogada", advogada_image: "Fotografia da advogada",
  hero_title: "Título", hero_text: "Texto", intro_text: "Texto de introdução",
  items: "Itens", closing_title: "Título de fechamento", closing_text: "Texto de fechamento",
  areas_interesse: "Áreas de interesse (separe por ;)", privacidade_text: "Aviso de privacidade (LGPD)",
  form_title: "Título do formulário", form_text: "Texto do formulário",
};

const inputCls = "w-full border border-navy/20 bg-white px-3.5 py-3 text-sm text-navy focus:border-gold";

function labelFor(k) {
  return FIELD_LABELS[k] || k;
}

export default function AdminContent() {
  const [pageKey, setPageKey] = useState("home");
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get(`/content/${pageKey}`)
      .then((r) => live && setSections(r.data.sections || {}))
      .catch(() => live && setSections({}))
      .finally(() => live && setLoading(false));
  }, [pageKey]);

  const setField = (k, v) => setSections((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${pageKey}`, { sections });
      toast.success("Alterações salvas. O site já exibe o novo conteúdo.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const items = Array.isArray(sections.items) ? sections.items : [];

  return (
    <div data-testid="admin-content-editor">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <h1 className="font-display text-3xl text-navy">Páginas</h1>
        <button
          data-testid="admin-content-save"
          onClick={save}
          disabled={saving || loading}
          className="inline-flex items-center gap-2 bg-navy px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory hover:bg-navy-2 disabled:opacity-60"
        >
          <Save size={14} /> {saving ? "Salvando…" : "Salvar alterações"}
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {PAGES.map((p) => (
          <button
            key={p.key}
            data-testid={`admin-page-${p.key}`}
            onClick={() => setPageKey(p.key)}
            className={`border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
              pageKey === p.key ? "border-gold bg-gold text-navy" : "border-navy/15 bg-white text-navy/70 hover:border-gold"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-inkgray">Carregando…</p>
      ) : (
        <div className="mt-8 space-y-6">
          {Object.entries(sections).map(([k, v]) => {
            if (k === "items") return <ItemsEditor key={k} items={items} setItems={(it) => setField("items", it)} />;
            if (k.endsWith("_image"))
              return <ImageField key={k} testid={`admin-field-${k}`} label={labelFor(k)} value={v} onChange={(val) => setField(k, val)} />;
            if (k === "marquee_words" || k === "areas_interesse")
              return (
                <div key={k} className="border border-navy/10 bg-white p-5">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">{labelFor(k)}</p>
                  <input className={inputCls} data-testid={`admin-field-${k}`} value={v || ""} onChange={(e) => setField(k, e.target.value)} />
                </div>
              );
            const long = typeof v === "string" && (v.length > 90 || ["_text", "_intro"].some((sfx) => k.endsWith(sfx)));
            return (
              <div key={k} className="border border-navy/10 bg-white p-5">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">{labelFor(k)}</p>
                {long ? (
                  <textarea rows={4} className={inputCls} data-testid={`admin-field-${k}`} value={v || ""} onChange={(e) => setField(k, e.target.value)} />
                ) : (
                  <input className={inputCls} data-testid={`admin-field-${k}`} value={v || ""} onChange={(e) => setField(k, e.target.value)} />
                )}
              </div>
            );
          })}
          {Object.keys(sections).length === 0 && <p className="text-sm text-inkgray">Nenhum campo para esta página.</p>}
        </div>
      )}
    </div>
  );
}

function ItemsEditor({ items, setItems }) {
  const update = (i, part, val) => {
    const next = items.map((it, j) => (j === i ? { ...it, [part]: val } : it));
    setItems(next);
  };
  const remove = (i) => setItems(items.filter((_, j) => j !== i));
  const add = () => setItems([...items, { title: "Novo item", text: "" }]);

  return (
    <div className="border border-navy/10 bg-white p-5" data-testid="admin-items-editor">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Itens da página (título + texto)</p>
      <div className="mt-4 space-y-4">
        {items.map((item, i) => (
          <div key={i} className="border border-navy/10 p-4">
            <div className="flex items-center gap-3">
              <input className={inputCls} value={item.title || ""} placeholder="Título" data-testid={`admin-item-title-${i}`} onChange={(e) => update(i, "title", e.target.value)} />
              <button data-testid={`admin-item-remove-${i}`} onClick={() => remove(i)} aria-label="Remover item" className="shrink-0 border border-red-200 p-2.5 text-red-600 transition-colors hover:bg-red-50">
                <Trash2 size={14} />
              </button>
            </div>
            <textarea rows={2} className={`${inputCls} mt-3`} value={item.text || ""} placeholder="Texto" data-testid={`admin-item-text-${i}`} onChange={(e) => update(i, "text", e.target.value)} />
          </div>
        ))}
      </div>
      <button data-testid="admin-item-add" onClick={add} className="mt-4 inline-flex items-center gap-2 border border-navy/20 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-navy hover:border-gold">
        <Plus size={14} /> Adicionar item
      </button>
    </div>
  );
}
